import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetEmailsByPartyIdsQuery, useGetPartyMasterCustomQuery, useUpdatePartyMasterMutation } from '../api/sendEmail.api';
import { useSelector } from 'react-redux';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../components/snackBar/snakbar';
import { Recipient } from './useSendEmail';

export interface SendEmailModalProps {
  open: boolean;
  onClose: () => void;
  pageOptions: string[];
  initialPageType?: string;
  recipients?: Recipient[];
  onUpdateEmail: (id: number, newEmail: string) => void;
  setIsEditRecipient?: (id: number) => void;
  removeRecipient: (id: number) => void;
  isEditRecipient?: number | null;
}

export function useSendEmailModal(pageOptions: string[]) {
  const [open, setOpen] = useState(false);
  const [initialPageType, setInitialPageType] = useState<string>('');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [partyIDs, setPartyIDs] = useState<number[]>([]);
  const [isEditRecipient, setIsEditRecipient] = useState<number | null>(null);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | undefined>(undefined);

  const openModal = useCallback((pageType: string, newRecipients: Recipient[], onCloseCb?: () => void) => {
    setInitialPageType(pageType);
    const partyIds = newRecipients.map((recipient) => recipient.recipientId);
    setPartyIDs(partyIds);
    setRecipients(newRecipients);
    setOnCloseCallback(() => onCloseCb);
    setOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpen(false);
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(undefined);
      setRecipients([]);
      setIsEditRecipient(null);
      setPartyIDs([]);
    }
  }, [onCloseCallback]);

  const {
    data: partyData = [],
  } = useGetPartyMasterCustomQuery({ partyid: Number(isEditRecipient) }, { refetchOnMountOrArgChange: false, skip: !isEditRecipient });


  const { data: recipientDetails } = useGetEmailsByPartyIdsQuery({ partyIds: partyIDs }, { skip: !partyIDs.length });


  useEffect(() => {
    if (recipientDetails) {
      const updatedRecipients = recipients.map((recipient) => {
        const matchingRecipient = recipientDetails.find((details) => details?.partyid === recipient.recipientId);
        if (matchingRecipient) {
          return { 
            ...recipient, 
            name: !recipient.name ? matchingRecipient?.partydisplayname || '' : recipient.name, 
            email: matchingRecipient.email, 
            // For POS invoices, don't check is_active status as they are not regular parties
            // Check if this is a POS invoice by looking at the invoice number pattern
            isActive: recipient.invoiceNumber?.startsWith('POS') ? 1 : matchingRecipient.is_active 
          };
        } else {
          // If no matching recipient found (common for POS invoices), treat as active
          return {
            ...recipient,
            isActive: 1, // Default to active for POS invoices or when party data is not found
            // For POS invoices, if email is empty, keep it empty but still mark as active
            email: recipient.email || ''
          };
        }
      });
      setRecipients(updatedRecipients);
    } else {
      // If no recipient details are available (common for POS invoices), ensure all recipients are active
      const updatedRecipients = recipients.map((recipient) => ({
        ...recipient,
        isActive: 1, // Default to active when no party data is available
        email: recipient.email || ''
      }));
      setRecipients(updatedRecipients);
    }
  }, [recipientDetails,partyIDs]);

  const currentFY = useSelector((state: any) => state.userPrefrenceandReducer?.financialYear);

  const [updatePartyMaster] = useUpdatePartyMasterMutation();

  const updateEmail = useCallback(
    async (id: number, newEmail: string) => {
      try {
        // Find the recipient to check if it's a POS invoice
        const recipient = recipients.find((r) => r.recipientId === id);
        const isPOSInvoice = recipient?.invoiceNumber?.startsWith('POS');
        
        if (isPOSInvoice) {
          // For POS invoices, just update the local state without updating party master
          setRecipients((prev) => prev.map((recipient) => (recipient.recipientId === id ? { ...recipient, email: newEmail } : recipient)));
          // No success message needed for POS invoices as it's just local storage
        } else {
          // For regular invoices, update the party master
          if (!partyData?.[0]) {
            return showErrorSnackbar('Party data not found', '', '');
          }
          setRecipients((prev) => prev.map((recipient) => (recipient.recipientId === id ? { ...recipient, email: newEmail } : recipient)));
          const input = {
            ...partyData?.[0],
            email: newEmail,
            companybranchid: 0,
            expiredtimestamp: 0,
            financialyearid: currentFY?.financialyearid,
            lastupdatedby: currentFY?.userid,
          };
          if (input?.enteredtimestamp) {
            delete input?.enteredtimestamp;
          }
          await updatePartyMaster({ createPartyMasterInput: input }).unwrap();
          showSuccessSnackbar('Email updated successfully', 'right', 'bottom');
        }
      } catch (error) {
        console.error('Error updating email:', error);
        showErrorSnackbar('Failed to update email', 'right', 'bottom');
      } finally {
        setIsEditRecipient(null);
      }
    },
    [partyData, currentFY, recipients]
  );

  const removeRecipient = useCallback((id: number) => {
    setRecipients((prev) => {
      const filtered = prev.filter((recipient) => recipient.id !== id);
      if (filtered.length === 0) {
        setOpen(false);
      }
      return filtered;
    });
  }, []);

  const modalProps = useMemo<SendEmailModalProps>(
    () => ({
      open,
      onClose: closeModal,
      pageOptions,
      initialPageType,
      recipients,
      onUpdateEmail: updateEmail,
      isEditRecipient,
      setIsEditRecipient,
      removeRecipient,
    }),
    [open, closeModal, pageOptions, initialPageType, recipients, updateEmail, isEditRecipient]
  );

  return { openModal, closeModal, modalProps };
}
